import { Link } from "@tanstack/react-router";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  IdCardIcon,
  LogOutIcon,
  StarIcon,
  SettingsIcon,
  UserIcon,
} from "lucide-react";
import { cloneElement, useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { ConfirmDialog } from "@/components/shared/dialogs/confirm-dialog";
import { SessionManagementDialog } from "@/components/shared/dialogs/session-management-dialog";
import { useAuth } from "@/features/auth/use-auth";
import { hasPermission } from "@/lib/authorization/permissions";

function getInitials(fullName: string): string {
  return fullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

export function ProfileMenu() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSignOutConfirmOpen, setIsSignOutConfirmOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isSessionsOpen, setIsSessionsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const initials = getInitials(user?.fullName ?? "User");

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsLanguageOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (isLanguageOpen) {
          setIsLanguageOpen(false);
        } else {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isLanguageOpen, isOpen]);

  function closeMenu() {
    setIsOpen(false);
    setIsLanguageOpen(false);
  }

  return (
    <div
      ref={menuRef}
      className="relative shrink-0 border-l border-nav-border pl-3 sm:pl-4"
    >
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className="flex items-center gap-2 rounded-md p-1.5 text-left transition-colors hover:bg-nav-item-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full border border-nav-light-border bg-bg-elevated text-xs font-semibold text-nav-muted sm:h-10 sm:w-10">
          {initials || "U"}
        </span>
        <span className="hidden max-w-40 flex-col truncate text-left sm:flex">
          <span className="truncate text-base font-medium leading-normal text-nav-text">
            {user?.fullName ?? t("common:profile.userFallback")}
          </span>
          <span className="truncate text-xs leading-normal text-nav-muted">
            {user?.tenantName ?? t("common:profile.companyFallback")}
          </span>
        </span>
        <ChevronDownIcon aria-hidden="true" className="h-5 w-5 text-nav-text" />
      </button>

      {isOpen ? (
        <ProfileMenuContent
          isLanguageOpen={isLanguageOpen}
          onClose={closeMenu}
          onToggleLanguage={() => setIsLanguageOpen((open) => !open)}
          onManageSessions={() => {
            closeMenu();
            setIsSessionsOpen(true);
          }}
          onSignOut={() => {
            closeMenu();
            setIsSignOutConfirmOpen(true);
          }}
          showSettings={hasPermission(user, "settings", "view")}
        />
      ) : null}
      <ConfirmDialog
        open={isSignOutConfirmOpen}
        title={t("common:auth.signOutConfirmTitle")}
        description={t("common:auth.signOutConfirmDescription")}
        confirmLabel={t("common:auth.signOut")}
        isDangerous
        onConfirm={() => {
          setIsSignOutConfirmOpen(false);
          void logout();
        }}
        onCancel={() => setIsSignOutConfirmOpen(false)}
      />
      <SessionManagementDialog
        open={isSessionsOpen}
        onClose={() => setIsSessionsOpen(false)}
      />
    </div>
  );
}

interface ProfileMenuContentProps {
  isLanguageOpen: boolean;
  onClose: () => void;
  onToggleLanguage: () => void;
  onManageSessions: () => void;
  onSignOut: () => void;
  showSettings: boolean;
}

function ProfileMenuContent({
  isLanguageOpen,
  onClose,
  onToggleLanguage,
  onManageSessions,
  onSignOut,
  showSettings,
}: ProfileMenuContentProps) {
  const { t } = useTranslation();

  return (
    <div
      role="menu"
      aria-label={t("common:profile.menuLabel")}
      className="absolute right-0 top-full z-50 mt-2 w-56 rounded-md border border-border bg-bg-elevated p-1 shadow-md sm:w-64"
    >
      <ProfileMenuItem
        icon={<UserIcon />}
        label={t("common:profile.myProfile")}
        onClick={onClose}
        to="/profile/basic-information"
      />
      {showSettings ? (
        <ProfileMenuItem
          icon={<SettingsIcon />}
          label={t("common:profile.settings")}
          onClick={onClose}
          to="/settings"
        />
      ) : null}
      <div className="relative">
        <button
          type="button"
          role="menuitem"
          aria-haspopup="menu"
          aria-expanded={isLanguageOpen}
          onClick={onToggleLanguage}
          className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-text-secondary outline-none transition-colors hover:bg-bg-surface hover:text-text-primary focus-visible:ring-2 focus-visible:ring-ring sm:text-base"
        >
          <span aria-hidden="true" className="text-lg leading-none">
            🇺🇸
          </span>
          <span>{t("common:profile.language")}</span>
          <ChevronRightIcon aria-hidden="true" className="ml-auto h-4 w-4" />
        </button>
        {isLanguageOpen ? <LanguageMenu onSelect={onClose} /> : null}
      </div>
      <ProfileMenuItem
        icon={<IdCardIcon />}
        label={t("common:profile.manageSessions")}
        onClick={onManageSessions}
      />
      <ProfileMenuItem
        icon={<StarIcon className="mr-2" />}
        label={t("common:profile.sendFeedback")}
        onClick={onClose}
      />
      <div className="my-1 border-t border-border" />
      <button
        type="button"
        role="menuitem"
        onClick={onSignOut}
        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-action-danger transition-colors hover:bg-action-danger/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-base"
      >
        <LogOutIcon aria-hidden="true" className="mr-2 h-4 w-4" />
        {t("common:auth.signOut")}
      </button>
    </div>
  );
}

interface ProfileMenuItemProps {
  icon: ReactElement;
  label: string;
  onClick: () => void;
  to?: "/profile/basic-information" | "/settings";
}

function ProfileMenuItem({ icon, label, onClick, to }: ProfileMenuItemProps) {
  if (to) {
    return (
      <Link
        to={to}
        role="menuitem"
        onClick={onClick}
        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-text-secondary transition-colors hover:bg-bg-surface hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-base"
      >
        {cloneElement(icon, {
          "aria-hidden": true,
          className: "mr-2 h-4 w-4",
        })}
        {label}
      </Link>
    );
  }

  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-text-secondary transition-colors hover:bg-bg-surface hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-base"
    >
      {cloneElement(icon, {
        "aria-hidden": true,
        className: "mr-2 h-4 w-4",
      })}
      {label}
    </button>
  );
}

interface LanguageMenuProps {
  onSelect: () => void;
}

function LanguageMenu({ onSelect }: LanguageMenuProps) {
  const { t } = useTranslation();

  return (
    <div
      role="menu"
      aria-label={t("common:profile.languageMenuLabel")}
      className="absolute right-full top-0 z-50 mr-1 w-48 overflow-hidden rounded-md border border-border bg-bg-elevated p-1 text-text-primary shadow-lg"
    >
      <button
        type="button"
        role="menuitem"
        aria-current="true"
        onClick={onSelect}
        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-text-secondary outline-none transition-colors hover:bg-bg-surface hover:text-text-primary focus-visible:ring-2 focus-visible:ring-ring sm:text-base"
      >
        <span aria-hidden="true" className="text-lg leading-none">
          🇺🇸
        </span>
        <span>{t("common:profile.english")}</span>
        <CheckIcon aria-hidden="true" className="ml-auto h-4 w-4 text-action-primary" />
      </button>
    </div>
  );
}
